import { z } from 'zod';
import { isoDateTime } from './common';

/**
 * contacts/{normalizedEmail} — the marketing/CRM contact base.
 *
 * A standalone directory of people MTC can reach (congress attendees imported
 * over 10 years, manual adds, future auto-feeds from inscriptions). Distinct
 * from `inscriptions` (actual formation registrations) — a contact has NO
 * formation attached. Doc ID = the lowercased email, so re-imports dedupe for
 * free. `phone` doubles as the WhatsApp number.
 */
export const contactSourceSchema = z.enum(['congres', 'formation', 'evenement', 'upload', 'manuel']);
export type ContactSource = z.infer<typeof contactSourceSchema>;

export const contactInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  /** WhatsApp / phone (same field). */
  phone: z.string().max(40).optional(),
  /** YYYY-MM-DD — powers Agenda birthday reminders. */
  birthdate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format YYYY-MM-DD').optional(),
  city: z.string().max(80).optional(),
  faculty: z.string().max(160).optional(),
  /** Admin-chosen segmentation labels. */
  tags: z.array(z.string().max(60)).default([]),
  /** How the contact entered the base (multi-source: same email can arrive via several channels). */
  sources: z.array(contactSourceSchema).default([]),
  notes: z.string().max(1000).optional(),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

export const contactSchema = contactInputSchema.extend({
  id: z.string(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Contact = z.infer<typeof contactSchema>;

/** Normalise an email for use as the contact's doc ID (dedup key). */
export function normalizeContactEmail(email: string): string {
  return email.trim().toLowerCase();
}
